import React, { useState } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Select,
  DatePicker,
  Input,
  message,
  Tag,
  Space,
  Divider,
} from "antd";
import {
  CheckOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Booking } from "../../types/booking";
import { supabase } from "../../lib/supabase";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

interface ContainerUnloadingProps {
  booking: Booking;
  onUnloadingUpdate?: (booking: Booking) => void;
}

interface UnloadingFormData {
  unloading_status: "pending" | "in_progress" | "completed";
  total_actual_cartons: number;
  total_actual_pallets: number;
  unloading_notes: string;
  unloaded_at?: string;
}

const ContainerUnloading: React.FC<ContainerUnloadingProps> = ({
  booking,
  onUnloadingUpdate,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<UnloadingFormData>({
    unloading_status: booking.unloading_status || "pending",
    total_actual_cartons: booking.total_actual_cartons || 0,
    total_actual_pallets: booking.total_actual_pallets || 0,
    unloading_notes: booking.unloading_notes || "",
    unloaded_at: booking.unloaded_at || undefined,
  });

  // Calculate estimated cartons from product info for comparison
  const getEstimatedCartons = (): number => {
    if (!booking.product_info) return 0;
    // Handle both Product and Product[] formats
    const productArray = Array.isArray(booking.product_info)
      ? booking.product_info.flat()
      : [booking.product_info].flat();

    return productArray.reduce((total, product) => {
      if (product && typeof product === "object" && "cartons" in product) {
        return total + (product.cartons || 0);
      }
      return total;
    }, 0);
  };

  const handleStatusChange = (
    status: "pending" | "in_progress" | "completed"
  ) => {
    setFormData((prev) => ({
      ...prev,
      unloading_status: status,
      unloaded_at:
        status === "completed" ? dayjs().toISOString() : prev.unloaded_at,
    }));

    if (status === "in_progress") {
      form.setFieldsValue({ unloaded_at: dayjs() });
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      const updateData = {
        unloading_status: formData.unloading_status,
        total_actual_cartons: formData.total_actual_cartons,
        total_actual_pallets: formData.total_actual_pallets,
        unloading_notes: formData.unloading_notes,
        unloaded_at:
          formData.unloading_status === "completed"
            ? formData.unloaded_at || dayjs().toISOString()
            : null,
      };

      const { error } = await supabase
        .from("bookings")
        .update(updateData)
        .eq("id", booking.id);

      if (error) throw error;

      message.success("Unloading information updated successfully");

      // Notify parent component
      if (onUnloadingUpdate) {
        const updatedBooking: Booking = {
          ...booking,
          ...updateData,
          unloaded_at: updateData.unloaded_at || undefined,
        };
        onUnloadingUpdate(updatedBooking);
      }
    } catch (error) {
      console.error("Error updating unloading info:", error);
      message.error("Failed to update unloading information");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <ClockCircleOutlined style={{ color: "#faad14" }} />;
      case "in_progress":
        return <PlayCircleOutlined style={{ color: "#1890ff" }} />;
      case "completed":
        return <CheckOutlined style={{ color: "#52c41a" }} />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "in_progress":
        return "processing";
      case "completed":
        return "success";
      default:
        return "default";
    }
  };

  const estimatedCartons = getEstimatedCartons();
  const variance = formData.total_actual_cartons - estimatedCartons;

  return (
    <Card
      title={
        <Space>
          📦 Container Unloading
          <Tag
            color={getStatusColor(formData.unloading_status)}
            icon={getStatusIcon(formData.unloading_status)}
          >
            {formData.unloading_status.replace("_", " ").toUpperCase()}
          </Tag>
        </Space>
      }
      size="small"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...formData,
          unloaded_at: formData.unloaded_at
            ? dayjs(formData.unloaded_at)
            : undefined,
        }}
        onValuesChange={(_, allValues) => {
          setFormData((prev) => ({
            ...prev,
            ...allValues,
            unloaded_at: allValues.unloaded_at
              ? allValues.unloaded_at.toISOString()
              : prev.unloaded_at,
          }));
        }}
      >
        <Form.Item label="Unloading Status" name="unloading_status">
          <Select onChange={handleStatusChange}>
            <Option value="pending">📋 Pending</Option>
            <Option value="in_progress">🚛 In Progress</Option>
            <Option value="completed">✅ Completed</Option>
          </Select>
        </Form.Item>

        {formData.unloading_status !== "pending" && (
          <Form.Item label="Unloaded At" name="unloaded_at">
            <DatePicker
              showTime
              style={{ width: "100%" }}
              disabled={formData.unloading_status === "completed"}
            />
          </Form.Item>
        )}

        <Divider>📊 Actual Counts</Divider>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <Form.Item
            label="Total Actual Cartons"
            name="total_actual_cartons"
            rules={[
              { required: true, message: "Please enter actual carton count" },
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Enter actual count"
            />
          </Form.Item>

          <Form.Item label="Total Actual Pallets" name="total_actual_pallets">
            <InputNumber
              min={0}
              style={{ width: "100%" }}
              placeholder="Enter actual count"
            />
          </Form.Item>
        </div>

        {estimatedCartons > 0 && (
          <div
            style={{
              background:
                variance === 0
                  ? "#f6ffed"
                  : Math.abs(variance) > estimatedCartons * 0.1
                  ? "#fff2f0"
                  : "#fff7e6",
              border:
                variance === 0
                  ? "1px solid #b7eb8f"
                  : Math.abs(variance) > estimatedCartons * 0.1
                  ? "1px solid #ffccc7"
                  : "1px solid #ffd591",
              borderRadius: "6px",
              padding: "8px 12px",
              marginBottom: "16px",
            }}
          >
            <div style={{ fontSize: "12px", color: "#666" }}>
              📝 Estimated: {estimatedCartons} cartons vs ⚖️ Actual:{" "}
              {formData.total_actual_cartons} cartons
            </div>
            {variance !== 0 && (
              <div
                style={{
                  fontSize: "12px",
                  color: variance > 0 ? "#389e0d" : "#cf1322",
                  fontWeight: "bold",
                }}
              >
                {variance > 0 ? "📈" : "📉"} Variance: {variance > 0 ? "+" : ""}
                {variance} cartons (
                {((variance / estimatedCartons) * 100).toFixed(1)}%)
              </div>
            )}
          </div>
        )}

        <Form.Item label="Unloading Notes" name="unloading_notes">
          <TextArea
            rows={3}
            placeholder="Notes about unloading process, discrepancies, damages, etc..."
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            onClick={handleSave}
            loading={loading}
            style={{ width: "100%" }}
          >
            💾 Save Unloading Information
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ContainerUnloading;
